import React from 'react';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import TableTree from '@atlaskit/table-tree';
import { useIntl } from '@atlassian/jira-intl';
import { messages } from './messages';
import type { Props, Item, Content, QuarterRangeInMonths, QuartersRangeInMonths } from './types';
import { getQuarterRanges, getQuarterStartOrEndDate } from './utils';

const Title = ({ title }: Content) => title;
const Description = ({ description }: Content) => description;

export const Preview = ({ fyStartMonth }: Props) => {
	const { formatMessage, formatDate } = useIntl();
	const quartersStartEnd: QuartersRangeInMonths = getQuarterRanges(fyStartMonth);

	const items: Item[] = quartersStartEnd.map(
		(quarter: QuarterRangeInMonths, index: number): Item => {
			const startMonth: Date = getQuarterStartOrEndDate(quarter[0]);
			const endMonth: Date = getQuarterStartOrEndDate(quarter[1]);
			return {
				id: `${index + 1}`,
				content: {
					title: `${index + 1}`,
					description: formatMessage(messages.quarterRange, {
						startMonth: formatDate(startMonth, { month: 'long' }),
						endMonth: formatDate(endMonth, { month: 'long' }),
					}),
				},
				hasChildren: false,
			};
		},
	);

	const quarterOneStart: Date = getQuarterStartOrEndDate(quartersStartEnd[0][0]);
	const quarterFourEnd: Date = getQuarterStartOrEndDate(quartersStartEnd[3][1], false);

	return (
		<>
			<Heading size="xsmall" as="h2">
				{formatMessage(messages.previewTitle)}
			</Heading>
			<Box xcss={tableWrapperStyles}>
				<TableTree
					columns={[Title, Description]}
					headers={[
						formatMessage(messages.quarterColumnHeading),
						formatMessage(messages.datesColumnHeading),
					]}
					columnWidths={['100px', '220px']}
					items={items}
				/>
			</Box>
			{formatMessage(messages.quarterDescription, {
				quarterOneStart: formatDate(quarterOneStart, {
					day: 'numeric',
					month: 'long',
				}),
				quarterFourEnd: formatDate(quarterFourEnd, {
					day: 'numeric',
					month: 'long',
				}),
			})}
		</>
	);
};

const tableWrapperStyles = xcss({
	marginTop: 'space.200',
	marginBottom: 'space.300',
});
